import React, { useState, useEffect, useCallback } from "react";
import { ClipboardCheck, Check, X, Sliders, Trophy, TrendingDown, Building2, Briefcase, ChevronRight, CheckCircle2, Circle } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { BranchFilter, PeriodPicker } from "../../components/finance";

const TABS = [
  { id: "approvals", label: "Persetujuan" },
  { id: "summary", label: "Ringkasan Kepatuhan" },
  { id: "overview", label: "Overview (Owner)" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const Bar = ({ v }) => <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-200"><div className="h-full bg-primary" style={{ width: `${v || 0}%` }} /></div>;

export default function HRCompliance() {
  const { user } = useAuth();
  const toast = useToast();
  const isOwner = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const [tab, setTab] = useState("approvals");
  return (
    <div data-testid="hr-compliance-page">
      <PageHeader title="Kepatuhan Jobdesk" subtitle="Persetujuan checklist harian & tren kepatuhan operasional" icon={ClipboardCheck} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="hr-compliance-tabs">
        {TABS.filter((t) => t.id !== "overview" || isOwner).map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hr-compliance-tab-${t.id}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-primary text-white" : "bg-stone-100 text-ink-soft hover:bg-stone-200"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "approvals" && <Approvals user={user} toast={toast} />}
      {tab === "summary" && <Summary user={user} toast={toast} />}
      {tab === "overview" && isOwner && <Overview toast={toast} />}
    </div>
  );
}

function Approvals({ user, toast }) {
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const [branchId, setBranchId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/checklists", { params: { status: "pending", date, branch_id: branchId || undefined } }); setRows(data.checklists); }
    catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [date, branchId, toast]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-compliance-branch" />}
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="hr-compliance-date" className="!w-44" />
      </div>
      {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <div className="space-y-2" data-testid="hr-compliance-approvals">
          {rows.length === 0 ? <Card><p className="py-6 text-center text-sm text-ink-mute">Tidak ada checklist menunggu persetujuan.</p></Card> : rows.map((r) => (
            <Card key={r.id} className="!py-3" data-testid={`hr-checklist-${r.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-ink">{r.employee_name} <span className="text-xs text-ink-mute">· {r.position} · {r.branch_name}</span></p>
                  <p className="text-xs text-ink-mute">{r.mandatory_done}/{r.mandatory_total} tugas wajib · {r.date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone={r.compliance >= 100 ? "success" : r.compliance >= 50 ? "warn" : "danger"}>{r.compliance}%</Badge>
                  <button onClick={() => setDetail(r)} className="rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" data-testid={`hr-checklist-view-${r.id}`}><ChevronRight size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <ReviewModal checklist={detail} onClose={() => setDetail(null)} onDone={() => { setDetail(null); load(); }} toast={toast} />
    </div>
  );
}

function ReviewModal({ checklist, onClose, onDone, toast }) {
  const [reason, setReason] = useState("");
  const [adjust, setAdjust] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setAdjust(""); setNote(""); }, [checklist]);
  if (!checklist) return null;

  const review = async (decision) => {
    if (decision === "reject" && !reason.trim()) return toast.error("Alasan penolakan wajib diisi");
    if (decision === "approve" && adjust !== "" && !note.trim()) return toast.error("Catatan wajib saat menyesuaikan skor");
    setBusy(true);
    try {
      await api.patch(`/hr/checklists/${checklist.id}/review`, {
        decision, reason: reason || undefined,
        adjusted_compliance: adjust !== "" ? Number(adjust) : undefined, note: note || undefined });
      toast.success(decision === "approve" ? "Checklist disetujui" : "Checklist ditolak"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Review — ${checklist.employee_name}`} size="md">
      <div className="space-y-3" data-testid="hr-checklist-review-modal">
        <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3 py-2">
          <span className="text-sm text-ink-soft">{checklist.position} · {checklist.branch_name} · {checklist.date}</span>
          <Badge tone="info">{checklist.compliance}%</Badge>
        </div>
        <div className="space-y-1.5 max-h-60 overflow-y-auto">
          {checklist.tasks.map((t) => (
            <div key={t.key} className="flex items-start gap-2 text-sm">
              {t.completed ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" /> : <Circle size={16} className="mt-0.5 shrink-0 text-ink-mute" />}
              <div>
                <span className={t.completed ? "text-ink" : "text-ink-mute"}>{t.task}</span>
                {t.mandatory ? <span className="ml-1 text-[10px] font-bold text-amber-600">WAJIB</span> : <span className="ml-1 text-[10px] text-ink-mute">opsional</span>}
                {t.note && <p className="text-xs text-ink-mute">Catatan: {t.note}</p>}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Sesuaikan skor (opsional)" type="number" placeholder={`${checklist.compliance}`} value={adjust} onChange={(e) => setAdjust(e.target.value)} data-testid="hr-checklist-adjust" />
          <Input label="Catatan Manajer" value={note} onChange={(e) => setNote(e.target.value)} data-testid="hr-checklist-note" />
        </div>
        <Input label="Alasan penolakan (jika ditolak)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="hr-checklist-reason" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => review("reject")} loading={busy} data-testid="hr-checklist-reject"><X size={15} /> Tolak</Button>
          <Button onClick={() => review("approve")} loading={busy} data-testid="hr-checklist-approve"><Check size={15} /> Setujui</Button>
        </div>
      </div>
    </Modal>
  );
}

function Summary({ user, toast }) {
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const [branchId, setBranchId] = useState("");
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/compliance/summary", { params: { period, branch_id: branchId || undefined } }); setRows(data.rows); }
    catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [period, branchId, toast]);
  useEffect(() => { load(); }, [load]);

  const pct = (v) => v == null ? "—" : `${v}%`;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-compliance-summary-branch" />}
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      {loading ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <Card className="overflow-x-auto" data-testid="hr-compliance-summary">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <tr><th className="py-2 pr-3">Karyawan</th><th className="py-2 pr-3">Jabatan</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3 text-right">Hari Ini</th><th className="py-2 pr-3 text-right">Mingguan</th><th className="py-2 pr-3 text-right">Bulanan</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.employee_id} data-testid={`hr-compliance-row-${r.employee_id}`}>
                  <td className="py-2.5 pr-3 font-medium text-ink">{r.employee_name}</td>
                  <td className="py-2.5 pr-3 text-ink-soft">{r.position}</td>
                  <td className="py-2.5 pr-3 text-ink-soft">{r.branch_name || "—"}</td>
                  <td className="py-2.5 pr-3 text-right">{pct(r.today)}</td>
                  <td className="py-2.5 pr-3 text-right">{pct(r.weekly)}</td>
                  <td className="py-2.5 pr-3 text-right font-semibold text-ink">{pct(r.monthly)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-ink-mute">Belum ada data.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function Overview({ toast }) {
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/hr/compliance/overview", { params: { period } }); setD(data); }
    catch { toast.error("Gagal"); } finally { setLoading(false); }
  }, [period, toast]);
  useEffect(() => { load(); }, [load]);
  if (loading || !d) return <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>;

  const List = ({ title, icon: Icon, rows, keyField, color }) => (
    <Card>
      <h3 className={`mb-2 flex items-center gap-2 font-heading font-bold ${color}`}><Icon size={16} /> {title}</h3>
      {rows.length === 0 ? <p className="py-2 text-sm text-ink-mute">Belum ada data.</p> : rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between border-b border-line py-1.5 text-sm last:border-0">
          <span className="text-ink-soft">{r[keyField]}{r.employee_name ? ` · ${r.position || ""}` : ""}</span>
          <span className="font-semibold text-ink">{r.compliance}%</span>
        </div>
      ))}
    </Card>
  );

  return (
    <div className="space-y-3" data-testid="hr-compliance-overview">
      <div className="flex items-center justify-between">
        <PeriodPicker value={period} onChange={setPeriod} />
        <Badge tone="info">Rata-rata {d.overall}% · {d.total_employees} karyawan</Badge>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <List title="Kepatuhan Tertinggi" icon={Trophy} rows={d.top.map((x) => ({ ...x, _k: x.employee_name }))} keyField="employee_name" color="text-emerald-600" />
        <List title="Kepatuhan Terendah" icon={TrendingDown} rows={d.lowest.map((x) => ({ ...x, _k: x.employee_name }))} keyField="employee_name" color="text-red-600" />
        <List title="Tren per Cabang" icon={Building2} rows={d.by_branch} keyField="branch" color="text-ink" />
        <List title="Tren per Jabatan" icon={Briefcase} rows={d.by_position} keyField="position" color="text-ink" />
      </div>
    </div>
  );
}
